/**
 * Boîte à outils — icônes des outils et listes pour les ouvrir : lignes
 * (icône, nom, une phrase) dans la boîte et sur la page d'une SKILLCARD,
 * pastilles compactes dans la fenêtre « Cours » du player.
 */

import {
  ChartColumn,
  ChartLine,
  ChartNoAxesColumn,
  ChartScatter,
  ChartSpline,
  ChevronRight,
  Clock3,
  Columns3,
  Dices,
  DoorOpen,
  Grid3x3,
  Hourglass,
  LayoutGrid,
  MoveDiagonal,
  MoveUpRight,
  Network,
  Workflow,
  Percent,
  Pyramid,
  Radius,
  Brackets,
  CircleDot,
  Spline,
  Activity,
  Landmark,
  Circle,
  Waves,
  ChartArea,
  Amphora,
  Brain,
  Scale,
  Blocks,
  BrickWall,
  Table2,
  Superscript,
  FlipHorizontal2,
  DraftingCompass,
  Diff,
  Ship,
  Sigma,
  Split,
  Square,
  SquareRadical,
  TrendingUp,
  TriangleRight,
  Orbit,
  Waypoints,
  Grid2x2,
  Binary,
  SquareTerminal,
  Ruler,
  SquareDashed,
  Bot,
  PackageOpen,
  Slice,
  GitCommitHorizontal,
  ChartColumnBig,
  Grid2x2Plus,
  PencilRuler,
  TrendingUpDown,
  UsersRound,
  Rainbow,
  Tangent,
  FerrisWheel,
  ChartNoAxesGantt,
  Scissors,
  Wind,
  SlidersHorizontal,
  Axis3d,
  TableProperties,
  Route,
  Mountain,
  Infinity as InfinityIcon,
  Move3d,
  Scaling,
  FlaskConical,
  Zap,
  Atom,
  Telescope,
  AudioWaveform,
  Gauge,
  Beaker,
  Snowflake,
  Syringe,
  Palette,
  BatteryCharging,
  Hexagon,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { ToolDef, ToolId } from './catalog';

export const TOOL_ICONS: Record<ToolId, LucideIcon> = {
  'times-table': Grid3x3,
  'pythagoras-squares': Square,
  thales: Split,
  'right-triangle-trig': TriangleRight,
  'line-sliders': ChartLine,
  'function-plotter': TrendingUp,
  'trig-circle': Radius,
  'vector-sum': MoveUpRight,
  'dot-product': MoveDiagonal,
  sequences: ChartScatter,
  'partial-sums': Sigma,
  'pascal-triangle': Pyramid,
  'random-launcher': Dices,
  'bayes-tree': Network,
  'galton-board': ChartColumn,
  'stats-series': ChartNoAxesColumn,
  'percent-evolutions': Percent,
  'primes-grid': LayoutGrid,
  'place-value': Columns3,
  'duration-clock': Hourglass,
  'number-machine': Workflow,
  'coordinates-battleship': Ship,
  'root-square': SquareRadical,
  'interval-builder': Brackets,
  'number-sets': CircleDot,
  'moving-points': Spline,
  'reference-functions': Activity,
  'compound-e': Landmark,
  'circle-equation': Circle,
  'chord-tangent': Waves,
  'riemann-sums': ChartArea,
  'urn-draws': Amphora,
  'smart-calc': Brain,
  balance: Scale,
  'algebra-tiles': Blocks,
  'fraction-wall': BrickWall,
  'signed-counters': Diff,
  'proportion-table': Table2,
  powers: Superscript,
  transformations: FlipHorizontal2,
  angles: DraftingCompass,
  'modular-clock': Clock3,
  taylor: ChartSpline,
  'complex-plane': Orbit,
  'markov-graph': Waypoints,
  'matrix-calc': Grid2x2,
  logic: Binary,
  'python-sandbox': SquareTerminal,
  'unit-converter': Ruler,
  geoboard: SquareDashed,
  'grid-robot': Bot,
  'solid-nets': PackageOpen,
  'solid-sections': Slice,
  'number-line': GitCommitHorizontal,
  'mean-leveling': ChartColumnBig,
  'area-multiplication': Grid2x2Plus,
  'ruler-compass': PencilRuler,
  'graphic-solve': TrendingUpDown,
  samples: UsersRound,
  parabola: Rainbow,
  tangent: Tangent,
  'lottery-wheel': FerrisWheel,
  concentration: ChartNoAxesGantt,
  bisection: Scissors,
  'direction-field': Wind,
  'slide-rule': SlidersHorizontal,
  'pi-polygon': Hexagon,
  'linear-map': Scaling,
  redox: FlaskConical,
  circuit: Zap,
  motion: Gauge,
  waves: AudioWaveform,
  optics: Telescope,
  atom: Atom,
  ph: Beaker,
  matter: Snowflake,
  gas: Syringe,
  colors: Palette,
  rc: BatteryCharging,
  'law-sim': DoorOpen,
  'gram-schmidt': Axis3d,
  'reaction-progress': TableProperties,
  lagrange: Route,
  'level-gradient': Mountain,
  'param-curve': InfinityIcon,
  'space-frame': Move3d,
};

/** Lignes « icône · nom · phrase › », une par outil. */
export function ToolList({
  title,
  tools,
  onOpen,
}: {
  title?: string;
  tools: readonly ToolDef[];
  onOpen: (id: ToolId) => void;
}) {
  return (
    <section className="space-y-2">
      {title && <h3 className="text-ink-500 text-xs font-bold uppercase tracking-wide">{title}</h3>}
      <ul className="space-y-2">
        {tools.map((t) => {
          const Icon = TOOL_ICONS[t.id];
          return (
            <li key={t.id}>
              <button
                type="button"
                onClick={() => onOpen(t.id)}
                className="border-ink-100 hover:bg-ink-50 flex min-h-16 w-full items-center gap-3 rounded-2xl border p-3 text-left transition-colors active:scale-[0.98]"
              >
                <span className="bg-primary-50 text-primary-600 flex h-11 w-11 shrink-0 items-center justify-center rounded-xl">
                  <Icon className="h-5 w-5" aria-hidden />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="text-ink-900 block font-semibold">{t.title}</span>
                  <span className="text-ink-500 block text-sm">{t.pitch}</span>
                </span>
                <ChevronRight className="text-ink-300 h-5 w-5 shrink-0" aria-hidden />
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/** Pastilles « icône · nom » (fenêtre Cours) ; aria « Ouvrir l'outil … ». */
export function ToolChips({
  tools,
  onOpen,
}: {
  tools: readonly ToolDef[];
  onOpen: (id: ToolId) => void;
}) {
  return (
    <ul className="flex flex-wrap gap-2">
      {tools.map((t) => {
        const Icon = TOOL_ICONS[t.id];
        return (
          <li key={t.id}>
            <button
              type="button"
              onClick={() => onOpen(t.id)}
              aria-label={`Ouvrir l’outil ${t.title}`}
              className="border-ink-200 text-ink-800 hover:bg-ink-50 inline-flex min-h-11 items-center gap-2 rounded-full border px-3 text-sm font-semibold active:scale-[0.98]"
            >
              <Icon className="text-primary-600 h-4 w-4 shrink-0" aria-hidden />
              {t.title}
            </button>
          </li>
        );
      })}
    </ul>
  );
}
